import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import { ArrowLeft } from "lucide-react";
import { apiService } from "../services/api";
import { ApplicationCreate } from "../types";

export default function CreateApplicationPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState<ApplicationCreate>({
    name: "",
    businessArea: "",
    division: "",
    description: "",
    applicationType: "",
    environment: "",
    deploymentUrl: "",
    status: "active",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: businessAreasResponse } = useQuery(
    "businessAreas",
    () => apiService.getBusinessAreas(),
    {
      select: (response) =>
        response.data.success ? (response.data.data as string[]) : [],
    },
  );

  const { data: divisionsResponse, isLoading: loadingDivisions } = useQuery(
    ["divisions", form.businessArea],
    () => apiService.getDivisions(form.businessArea),
    {
      enabled: !!form.businessArea,
      select: (response) =>
        response.data.success ? (response.data.data as string[]) : [],
    },
  );

  const mutation = useMutation(
    (data: ApplicationCreate) => apiService.createApplication(data),
    {
      onSuccess: (resp) => {
        const created = (resp.data as any)?.data;
        navigate(`/applications/${created?.id || ""}`);
      },
    },
  );

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.businessArea.trim()) e.businessArea = "Business area is required";
    if (!form.division.trim())
      e.division = "Division/Business Unit is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const payload: ApplicationCreate = {
      name: form.name,
      businessArea: form.businessArea,
      division: form.division,
    };
    if (form.description) payload.description = form.description;
    if (form.applicationType) payload.applicationType = form.applicationType;
    if (form.environment) payload.environment = form.environment;
    if (form.deploymentUrl) payload.deploymentUrl = form.deploymentUrl;
    if (form.status) payload.status = form.status;
    mutation.mutate(payload);
  };

  const handleChange = (field: keyof ApplicationCreate, value: string) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "businessArea") {
        updated.division = "";
      }
      return updated;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Link
        to="/applications"
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Applications
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        Create Application
      </h1>

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className={`input w-full ${errors.name ? "border-red-500" : ""}`}
            placeholder="My Application"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Business Area & Division */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Business Area <span className="text-red-500">*</span>
            </label>
            <select
              value={form.businessArea}
              onChange={(e) => handleChange("businessArea", e.target.value)}
              className={`input w-full ${errors.businessArea ? "border-red-500" : ""}`}
            >
              <option value="">Select business area...</option>
              {businessAreasResponse?.map((area) => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
            </select>
            {errors.businessArea && (
              <p className="mt-1 text-sm text-red-600">{errors.businessArea}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Division/Business Unit <span className="text-red-500">*</span>
            </label>
            <select
              value={form.division}
              onChange={(e) => handleChange("division", e.target.value)}
              className={`input w-full ${errors.division ? "border-red-500" : ""}`}
              disabled={!form.businessArea || loadingDivisions}
            >
              <option value="">
                {!form.businessArea
                  ? "Select a business area first"
                  : loadingDivisions
                    ? "Loading..."
                    : "Select division/business unit..."}
              </option>
              {divisionsResponse?.map((div) => (
                <option key={div} value={div}>
                  {div}
                </option>
              ))}
            </select>
            {errors.division && (
              <p className="mt-1 text-sm text-red-600">{errors.division}</p>
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={form.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
            rows={3}
            className="input w-full"
            placeholder="Brief description of the application..."
          />
        </div>

        {/* Type & Environment row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type
            </label>
            <select
              value={form.applicationType || ""}
              onChange={(e) => handleChange("applicationType", e.target.value)}
              className="input w-full"
            >
              <option value="">Select type...</option>
              <option value="microservice">Microservice</option>
              <option value="api">API</option>
              <option value="web-app">Web App</option>
              <option value="backend">Backend</option>
              <option value="frontend">Frontend</option>
              <option value="worker">Worker</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Environment
            </label>
            <select
              value={form.environment || ""}
              onChange={(e) => handleChange("environment", e.target.value)}
              className="input w-full"
            >
              <option value="">Select environment...</option>
              <option value="production">Production</option>
              <option value="staging">Staging</option>
              <option value="development">Development</option>
              <option value="test">Test</option>
            </select>
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status
          </label>
          <select
            value={form.status || "active"}
            onChange={(e) => handleChange("status", e.target.value)}
            className="input w-full"
          >
            <option value="active">Active</option>
            <option value="deprecated">Deprecated</option>
            <option value="retired">Retired</option>
          </select>
        </div>

        {/* Deployment URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Deployment URL
          </label>
          <input
            type="url"
            value={form.deploymentUrl || ""}
            onChange={(e) => handleChange("deploymentUrl", e.target.value)}
            className="input w-full"
            placeholder="https://..."
          />
        </div>

        {/* Error message */}
        {mutation.isError && (
          <p className="text-sm text-red-600">
            Failed to create application. Please try again.
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Link to="/applications" className="btn btn-secondary">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={mutation.isLoading}
            className="btn btn-primary"
          >
            {mutation.isLoading ? "Creating..." : "Create Application"}
          </button>
        </div>
      </form>
    </div>
  );
}
